import React from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  Typography,
  Box,
  Tooltip,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { fundingLabel, DEFAULT_FUNDING_TYPE, trainingStatusLabel, trainingTypeLabel } from '../../constants/trainings';

const statusStyles = {
  // "Planned" është ende-pa-nisur, pra neutral — i njëjti arsyetim si `not_started`
  // te StatusChip. Vjollca e bënte të dukej si gjendje aktive.
  Planned:      { bg: 'rgba(100,116,139,0.12)', color: '#475569' },
  'In progress':{ bg: 'rgba(59,130,246,0.1)',  color: '#2563EB' },
  Completed:    { bg: 'rgba(22,163,74,0.1)',   color: '#15803D' },
  Cancelled:    { bg: 'rgba(148,163,184,0.15)', color: '#475569' },
};

/**
 * Data numerike, jo muaji me shkronja.
 *
 * `toLocaleDateString('sq-AL', { month: 'short' })` kthen «Feb», «Mar», «May» —
 * shfletuesit nuk e mbajnë shkurtimin shqip të muajve, ndaj te një regjistër
 * krejt shqip dilnin emra muajsh anglisht. Formati `dd.mm` lexohet njësoj në
 * çdo gjuhë dhe është i njëjti që përdor pjesa tjetër e aplikacionit.
 */
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  const dd = String(dt.getDate()).padStart(2, '0');
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}`;
};

function TrainingRow({ training, onView }) {
  // Po ashtu i pandjeshëm ndaj shkronjave: «In Progress» nga të dhënat e
  // hershme binte te ngjyra neutrale dhe dukej si status i panjohur.
  const svKey = Object.keys(statusStyles).find(k => k.toLowerCase() === String(training.status || '').toLowerCase());
  const sv = statusStyles[svKey] || { bg: '#F1F5F9', color: '#475569' };
  const participants = training.participants || [];
  const pct = training.capacity > 0 ? Math.min(100, Math.round((participants.length / training.capacity) * 100)) : null;
  return (
    <TableRow hover sx={{ '&:hover': { bgcolor: '#FAFCFE' } }}>
      {/* Trajnimi */}
      <TableCell sx={{ py: 0.85, overflow: 'hidden' }}>
        <Typography variant="body2" fontWeight={700} fontSize="0.82rem" noWrap title={training.name}>{training.name}</Typography>
        {/* Financimi ishte kolonë e vetën prej 164px që tregonte të njëjtën
            fjalë te çdo rresht — «Paguhet nga kompania» shtatë herë — dhe
            pikërisht ajo gjerësi e nxirrte butonin e rreshtit jashtë ekranit.
            Nuk u hoq: shfaqet këtu kur NDRYSHON nga parazgjedhja, atëherë kur
            ka vërtet diçka për të thënë. */}
        <Typography variant="caption" color="text.secondary" noWrap>
          {[
            training.trainer,
            training.location,
            (training.fundingType && training.fundingType !== DEFAULT_FUNDING_TYPE)
              ? fundingLabel(training.fundingType)
              : null,
          ].filter(Boolean).join(' · ')}
        </Typography>
      </TableCell>
      {/* Lloji */}
      <TableCell sx={{ py: 0.85 }}>
        <Chip size="small" label={trainingTypeLabel(training.type)}
          sx={{ fontSize: '0.68rem', height: 20, bgcolor: training.type === 'Mandatory' ? '#FEF2F2' : '#F0F9FF', color: training.type === 'Mandatory' ? '#B91C1C' : '#0369A1', fontWeight: 600 }} />
      </TableCell>
      {/* Data — vetëm fillimi.
          Diapazoni «10.02 → 11.02» kërkonte dyfishin e gjerësisë dhe dilte i
          prerë te «10.02 → 1…», që nuk thotë asgjë. Fillimi është ajo që
          renditet dhe kërkohet; periudha e plotë rri te detajet dhe te shenja
          nën datë kur trajnimi zgjat më shumë se një ditë. */
      }
      <TableCell sx={{ py: 0.85, overflow: 'hidden' }}>
        <Typography variant="body2" fontSize="0.78rem" noWrap
          title={training.endDate && training.endDate !== training.startDate
            ? `${fmtDate(training.startDate)} – ${fmtDate(training.endDate)}`
            : undefined}
          sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {fmtDate(training.startDate)}
        </Typography>
        {training.endDate && training.endDate !== training.startDate && (
          <Typography variant="caption" noWrap sx={{ color: '#94A3B8', fontSize: '0.68rem' }}>
            deri {fmtDate(training.endDate)}
          </Typography>
        )}
      </TableCell>
      {/* Pjesëmarrës / kapaciteti */}
      <TableCell sx={{ py: 0.85 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="body2" fontSize="0.78rem" sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 28 }}>
            {participants.length}{training.capacity ? `/${training.capacity}` : ''}
          </Typography>
          {pct !== null && (
            <Box sx={{ width: 48, height: 4, bgcolor: '#E2E8F0', borderRadius: 99, overflow: 'hidden' }}>
              <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: pct >= 90 ? '#EF4444' : pct >= 60 ? '#F59E0B' : '#22C55E', borderRadius: 99 }} />
            </Box>
          )}
        </Stack>
      </TableCell>
      {/* Statusi */}
      <TableCell sx={{ py: 0.85 }}>
        <Chip size="small" label={trainingStatusLabel(training.status)}
          sx={{ bgcolor: sv.bg, color: sv.color, fontWeight: 700, fontSize: '0.7rem', height: 22, maxWidth: '100%' }} />
      </TableCell>
      {/* Veprime */}
      <TableCell align="right" sx={{
        py: 0.5,
        position: 'sticky', right: 0, zIndex: 1,
        bgcolor: 'background.paper', borderLeft: '1px solid', borderColor: 'divider',
        'tr:hover &': { bgcolor: '#FAFCFE' },
      }}>
        <Tooltip title="Shiko detajet" arrow>
          <IconButton size="small" onClick={() => onView(training.id)}
            sx={{ width: 28, height: 28, color: '#475569', '&:hover': { bgcolor: '#F1F5F9', color: '#1E293B' } }}>
            <VisibilityIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

export default function TrainingTable({ trainings, onView }) {
  if (!trainings.length) {
    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, py: 5 }}>
        <Typography align="center" color="text.secondary" variant="body2">Nuk u gjetën trajnime.</Typography>
      </Paper>
    );
  }
  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Box sx={{ overflowX: 'auto' }}>
        {/* `tableLayout: 'fixed'` sepse me shpërndarje automatike një emër i
            gjatë — «Kommunikim Efektiv dhe Menaxhim Konflikti» — e merrte
            kolonën e parë në 318px dhe e shtynte statusin jashtë ekranit.
            Tani gjerësitë janë vendim, dhe emri shkurtohet me tri pika. */}
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', width: '31%' }}>Trajnimi</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', width: '22%' }}>Lloji</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', width: '12%' }}>Data</TableCell>
              {/* Një pikë më e vogël: fjala është e gjata e kokës dhe pritej me dy piksela. */}
              <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', width: '15%' }}>Pjesëmarrës</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', width: '20%' }}>Statusi</TableCell>
              {/* Butoni i vetëm i rreshtit ngjitet djathtas: tabela kërkonte
                  1034px dhe dilte 356px jashtë ekranit, ndaj «Shiko detajet» —
                  e vetmja rrugë drejt pjesëmarrësve — nuk arrihej pa rrëshqitur. */}
              <TableCell align="right" sx={{
                fontWeight: 700, fontSize: '0.78rem', width: 56,
                position: 'sticky', right: 0, zIndex: 3,
                bgcolor: 'background.paper', borderLeft: '1px solid', borderColor: 'divider',
              }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {trainings.map(t => (
              <TrainingRow key={t.id} training={t} onView={onView} />
            ))}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}

